/* eslint-disable */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Button } from 'primereact/button';
import SearchIcon from '../../assets/icons/search.svg';
import SortIcon from '../../assets/icons/sort.svg';
import './RecentChanges.scss';
import api, { apiWithRetry } from '../../utils/api';
import buildRecentChangeCards from '../../utils/buildRecentChangeCards';

interface ChangeItem {
  id: number;
  user: string;
  userAvatar: string;
  metricName: string;
  month: string;
  oldValue: string;
  newValue: string;
  comment?: string | null;
  isComment: boolean;
  updated: string;
}

interface RecentChangesProps {
  filters?: {
    year?: number;
    customer?: number;
    category?: number;
    brand?: number;
    isSummaryView?: boolean;
  };
  onExpandChange?: (expanded: boolean) => void;
}

const monthMapReverse: Record<number, string> = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec'
};

const RecentChanges: React.FC<RecentChangesProps> = ({ filters, onExpandChange }) => {

  const [expanded, setExpanded] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(true);

  const [changes, setChanges] = useState<ChangeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    hasMore: true
  });

  const [limit] = useState(25);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'newest' | 'oldest' | 'month'>('newest');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const listRef = useRef<HTMLDivElement | null>(null);

  const handleToggleExpand = () => {
    const newState = !expanded;
    setExpanded(newState);

    onExpandChange?.(newState);
  };

  const fetchRecentChanges = async (
    pageNumber = 1,
    reset = false,
    sortValue = sort
  ) => {

    if (loading) return;

    try {

      setLoading(true);

      const res = await apiWithRetry(() =>
        api.get('/fec/recent-updates', {
          params: {
            page: pageNumber,
            limit,
            sort: sortValue,
            search: debouncedSearch,
            year: filters?.year,
            customerId: filters?.customer,
            categoryId: filters?.category,
            brandId: filters?.brand
          }
        })
      );

      if (res?.data?.success) {

        const apiData = res.data.data?.data || [];
        const totalPages = res.data.data?.totalPages || 1;

        const mapped: ChangeItem[] = apiData.map((item: any) => {
          const month = monthMapReverse[item.month] || '';
          const metricName = item.metricName || item.metric || '-';

          return {
            id: Number(item.id),
            user: item.userName,
            userAvatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(item.userName)}&background=2D3748&color=fff&rounded=true&size=40`,
            metricName,
            month,
            oldValue: formatNumber(item.oldValue),
            newValue: formatNumber(item.newValue),
            comment: item.comment,
            isComment: !!item.comment,
            updated: new Date(item.timestamp).toLocaleString()
          };
        });

        if (reset) {
          setChanges(mapped);
        } else {
          setChanges(prev => [...prev, ...mapped]);
        }

        setPagination({
          page: pageNumber,
          hasMore: pageNumber < totalPages
        });

      }

    } catch (err) {
      console.error('Recent updates API error:', err);
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (!filters || filters.isSummaryView) return;

    setPagination({ page: 1, hasMore: true });
    fetchRecentChanges(1, true);
  }, [filters, debouncedSearch]);

  useEffect(() => {
    if (!filters || filters.isSummaryView) return;

    const handleRefresh = () => {
      setPagination({ page: 1, hasMore: true });
      fetchRecentChanges(1, true);
    };

    window.addEventListener('recentChanges:refresh', handleRefresh);

    return () => {
      window.removeEventListener('recentChanges:refresh', handleRefresh);
    };
  }, [filters]);

  /* auto fetch if container not filled */
  useEffect(() => {
    if (!expanded) return;

    const el = listRef.current;
    if (!el || loading || !pagination.hasMore) return;

    if (el.scrollHeight <= el.clientHeight) {
      loadMore();
    }
  }, [changes]);

  const renderedCards = useMemo(() => {
    return buildRecentChangeCards(changes, expanded);
  }, [changes, expanded]);

  const loadMore = () => {
    if (!pagination.hasMore || loading) return;
    const nextPage = pagination.page + 1;

    setPagination(prev => ({
      ...prev,
      page: nextPage
    }));

    fetchRecentChanges(nextPage);
  };

  const formatNumber = (val: number | null | undefined) => {
    if (val === null || val === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(val);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (
      !loading &&
      pagination.hasMore &&
      target.scrollHeight - target.scrollTop <= target.clientHeight + 80
    ) {
      loadMore();
    }
  };

  return (
    <div className={`recent-changes-container`}>

      <div className="recent-changes-header">

        <h3>Recent Changes</h3>

        <div className="header-icons">

          <img
            src={SearchIcon}
            alt="Search"
            className="icon"
            onClick={() => setShowSearch(prev => !prev)}
          />

          <img
            src={SortIcon}
            alt="Sort"
            className="icon"
            onClick={() => setSortOpen(!sortOpen)}
          />

          {changes.length > 2 && (
            <i
              className={`pi ${auditExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
              onClick={() => setAuditExpanded(!auditExpanded)}
            />
          )}

        </div>

      </div>

      {sortOpen && (
        <div className="sort-dropdown">
          <div
            onClick={() => {
              setSortOpen(false);
              setSort('newest');
              setPagination({ page: 1, hasMore: true });
              fetchRecentChanges(1, true, 'newest');
            }}
          >
            Newer to Older
          </div>
          <div
            onClick={() => {
              setSortOpen(false);
              setSort('oldest');
              setPagination({ page: 1, hasMore: true });
              fetchRecentChanges(1, true, 'oldest');
            }}
          >
            Older to Newer
          </div>
          <div
            onClick={() => {
              setSortOpen(false);
              setSort('month');
              setPagination({ page: 1, hasMore: true });
              fetchRecentChanges(1, true, 'month');
            }}
          >
            Order by Month
          </div>
        </div>
      )}

      {showSearch && (
        <div className="search-box">
          <div className="search-input-wrapper">
            <input
              placeholder="Search changes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {search && (
              <i
                className="pi pi-times clear-icon"
                onClick={() => {
                  setSearch('');
                  setDebouncedSearch('');
                  
                }}
              />
            )}
          </div>
        </div>
      )}

      {auditExpanded && (
        <div
          ref={listRef}
          className="recent-changes-list"
          style={expanded ? { maxHeight: '100vh', overflowY: 'auto' } : {}}
          onScroll={expanded ? handleScroll : undefined}
        >
          {renderedCards}

          {changes.length > 2 && (
            <Button
              label={expanded ? 'View Less' : 'View All'}
              className="p-button-outlined view-all-btn"
              onClick={handleToggleExpand}
            />
          )}

          {loading && (
            <div className="recent-loader">
              Loading...
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default RecentChanges;